import React, { useEffect, useRef, useState } from 'react';
import { Joyride, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { Compass, X } from '@phosphor-icons/react';
import api from '@/api';
import { useNavigate, useLocation } from 'react-router-dom';

// Any component can kick off the product tour by firing this event — used by the
// "Product Tour" entry in the account (burger) menu, which lives in a different
// subtree to the <OnboardingTour> that owns the Joyride instance.
export const PRODUCT_TOUR_EVENT = 'fb-start-tour';
export const startProductTour = () => window.dispatchEvent(new CustomEvent(PRODUCT_TOUR_EVENT));

// Single kill switch for the whole tour (auto-prompt, ?tour=1 deep link, and the
// account-menu replay) — flip back to false to re-enable, no other call site changes.
const TOUR_DISABLED = true;

// Both apps use a hover-to-expand rail. Collapsed, the tour would be spotlighting
// anonymous 20px icons — so it pins the sidebar open for the duration.
export const SIDEBAR_PIN_EVENT = 'fb-pin-sidebar';
const pinSidebar = (pinned) =>
  window.dispatchEvent(new CustomEvent(SIDEBAR_PIN_EVENT, { detail: { pinned } }));

// Role-specific spotlight steps. Each step targets a `data-testid` selector.
const STEPS = {
  vc: [
    {
      target: '[data-testid="nav-portfolio"]',
      title: 'Portfolio',
      content: 'Every verified investment with live MoIC, MRR, runway and burn — pulled straight from the monthly MIS your founders submit. No spreadsheet chasing at quarter end.',
    },
    {
      // Merged with the floating bubble: same thread, two entry points.
      target: '[data-testid="nav-chat"]',
      openAiBubble: true,
      coHighlight: '[data-testid="ai-bubble-panel"]',
      title: 'AI Co-Pilot',
      content: 'Your portfolio chief-of-staff. Ask "shortlist the top 10 applicants for Lab32" or "who is under six months of runway" — it sees every program, applicant and metric you own. That\'s it open on the right: the floating bubble carries the same thread onto every screen.',
    },
    {
      target: '[data-testid="nav-programs"]',
      title: 'Watchlists',
      content: 'Run dealflow watchlists. Share a public apply link on LinkedIn (it renders a rich preview), then triage applicants on a drag-and-drop board or a flat table — your choice of view.',
    },
    {
      target: '[data-testid="nav-tracker"]',
      title: 'Tracker',
      content: 'Startups you are watching but have not invested in. They keep submitting updates, so you keep seeing the numbers move — long before a term sheet.',
    },
    {
      target: '[data-testid="nav-discover"]',
      title: 'Discover',
      content: 'Browse founders across the ecosystem, filter by sector, stage and geography, and push the interesting ones into your tracker.',
    },
    {
      target: '[data-testid="nav-invite"]',
      title: 'Invite',
      content: 'Pull a founder onto Founderboard directly, even if they have never heard of it. Once they accept, their metrics flow into your portfolio automatically.',
    },
    {
      target: '[data-testid="nav-reports"]',
      title: 'Reports',
      content: 'Generate LP-ready reports from live portfolio data — so the quarterly stops being a week of manual assembly.',
    },
    {
      target: '[data-testid="nav-group-general"]',
      title: 'Display settings',
      content: 'Switch your reporting currency, flip between light and dark themes, and sign out. Currency applies everywhere — portfolio, reports and applicant metrics all re-render in it.',
      placement: 'right',
    },
  ],
  incubator: [
    {
      target: '[data-testid="nav-portfolio"]',
      title: 'Companies',
      content: 'Every company you have taken through a cohort, with live MRR, runway and burn pulled from the monthly updates founders submit. Your alumni tracker, kept current for you.',
    },
    {
      target: '[data-testid="startup-ai-command-bar"]',
      title: 'FounderBoard AI',
      content: 'Search your portfolio and workspace pages, ask a contextual question, or expand the AI bar into a full conversation with history.', 
    },
    {
      target: '[data-testid="nav-programs"]',
      title: 'Programs',
      content: 'Your home base. Create cohorts, share public apply pages on LinkedIn (they render a rich preview), then triage applicants on a drag-and-drop board or a flat table — your choice of view.',
    },
    {
      target: '[data-testid="nav-tracker"]',
      title: 'Tracker',
      content: 'Startups you are watching but have not taken into a cohort yet. They keep submitting updates, so you see how they develop before the next intake.',
    },
    {
      target: '[data-testid="nav-discover"]',
      title: 'Discover',
      content: 'Browse founders across the ecosystem, filter by sector, stage and geography, and push promising ones into your tracker for the next cohort.',
    },
    {
      target: '[data-testid="nav-invite"]',
      title: 'Invite',
      content: 'Pull a founder onto Founderboard directly, even if they have never heard of it. Once they accept, their metrics flow in automatically.',
    },
    {
      target: '[data-testid="nav-reports"]',
      title: 'Reports',
      content: 'Generate cohort and stakeholder reports from live data — so demo-day decks and funder updates stop being a manual assembly job.',
    },
    {
      target: '[data-testid="nav-group-general"]',
      title: 'Display settings',
      content: 'Switch your reporting currency, flip between light and dark themes, and sign out. Currency applies everywhere — companies, reports and applicant metrics all re-render in it.',
      placement: 'right',
    },
  ],
  startup: [
    {
      target: '[data-testid="nav-dashboard"]',
      title: 'Your founder dashboard',
      content: 'Runway, MRR trend, burn, recent acceptances and VC invites in one place. It refreshes on its own — this is your morning check.',
    },
    {
      target: '[data-testid="startup-ai-command-bar"]',
      title: 'FounderBoard AI',
      content: 'Click the AI bar from any startup page to open a fresh contextual chat. Your previous conversations stay available in History inside the same popup.',
      placement: 'bottom',
    },
    {
      target: '[data-testid="nav-benchmarks"]',
      title: 'Benchmarks',
      content: 'See how your growth, burn and runway compare to startups at your stage and sector — so you know whether your numbers are actually good.',
    },
    {
      target: '[data-testid="nav-update"]',
      title: 'Monthly updates',
      content: 'Submit your monthly MIS here. Your investors and trackers see it instantly, and it feeds your dashboard and benchmarks. Skipping months hurts your visibility.',
    },
    {
      target: '[data-testid="nav-programs"]',
      title: 'Discover & apply',
      content: 'Browse open accelerator and incubator programs with their deadlines. One-click apply attaches your full profile, so you\'re always the strongest version of your application.',
    },
    {
      target: '[data-testid="nav-interactions"]',
      title: 'Interactions',
      content: 'Every touchpoint with investors — who viewed your profile, who tracked you, who invited you. Your relationship history in one thread.',
    },
    {
      target: '[data-testid="nav-group-services"]',
      title: 'Service offerings',
      content: 'Three add-ons live here: Services (pre-vetted CAs, lawyers, registrations), Founder Credit (debt and credit lines without the bank runaround), and Marketing (vetted agencies, brand tools and campaign metrics).',
    },
    {
      target: '[data-testid="nav-group-general"]',
      title: 'Display settings',
      content: 'Switch your reporting currency, flip between light and dark themes, and sign out. Currency applies everywhere — dashboard, benchmarks and updates all re-render in it.',
      placement: 'right',
    },
  ],
};

// Each role's accent, so the tour feels native to the app you're in rather than
// a bolted-on library popup.
const ROLE_ACCENT = { vc: '#6366f1', incubator: '#f59e0b', startup: '#10b981' };

// The closing step physically opens the burger menu and lights up the whole
// dropdown, so every row — including Product Tour — is visible while it's
// explained. Beats describing a menu the user can't see.
const ACCOUNT_MENU_CONTENT = {
  startup: {
    title: 'Profile, vault & billing',
    body: 'Profile is the page investors read — keep it current. Team invites your co-founders. Vault is your data room: upload decks and financials once, then share a tracked link instead of emailing attachments. Billing holds your plan — free for your first 6 months, with the countdown inside.',
  },
  vc: {
    title: 'Profile, team & billing',
    body: 'Profile is what founders see when you reach out. Team adds partners and analysts as Owner / Admin / Member, with volume pricing at 2+ and 6+ seats. Billing holds your 7-day trial countdown. Activity Logs shows who on your team moved what, and when.',
  },
  incubator: {
    title: 'Profile, team & billing',
    body: 'Profile is what founders see on your apply pages. Team invites cohort managers, mentors and admins, with volume pricing at 2+ and 6+ seats. Billing holds your 7-day trial countdown. Activity Logs shows who on your team moved what, and when.',
  },
};

const accountMenuStep = (role) => {
  const copy = ACCOUNT_MENU_CONTENT[role] || ACCOUNT_MENU_CONTENT.vc;
  return {
    target: '[data-testid="account-menu-dropdown"]',
    title: copy.title,
    content: copy.body,
    placement: 'left',
    requiresAccountMenu: true,
  };
};

// The last word: a direct pointer at the row that restarts all of this. Also used
// on its own for anyone who skips.
const PRODUCT_TOUR_STEP = {
  target: '[data-testid="account-menu-product-tour"]',
  title: 'Restart the tour here',
  content: 'That\'s everything. Whenever you want to run through it again, open this menu and pick "Product Tour" — it\'s always in the same place.',
  placement: 'left',
  requiresAccountMenu: true,
};

// Ask AccountMenu (a different subtree) to open or close its dropdown.
export const ACCOUNT_MENU_EVENT = 'fb-account-menu';
const setAccountMenu = (open) =>
  window.dispatchEvent(new CustomEvent(ACCOUNT_MENU_EVENT, { detail: { open } }));

// Same for the floating AI bubble — the AI Coach step opens the actual chat panel
// rather than just pointing at a closed button.
export const AI_BUBBLE_EVENT = 'fb-ai-bubble';
const setAiBubble = (open) =>
  window.dispatchEvent(new CustomEvent(AI_BUBBLE_EVENT, { detail: { open } }));

// The `before` hook blocks the step until the menu row actually exists in the DOM,
// so the spotlight never lands on a stale position.
// Joyride spotlights exactly one element. When a step is about two places at once
// (AI Coach: the nav row and the floating bubble), the second gets lifted above
// the overlay and ringed instead.
const setCoHighlight = (selector, on) => {
  const el = selector ? document.querySelector(selector) : null;
  if (el) el.classList.toggle('fb-tour-colight', on);
};
const clearCoHighlights = () =>
  document.querySelectorAll('.fb-tour-colight').forEach(el => el.classList.remove('fb-tour-colight'));

const waitForElement = (selector, timeout = 2000) =>
  new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      if (document.querySelector(selector) || Date.now() - started > timeout) resolve();
      else requestAnimationFrame(tick);
    };
    tick();
  });

// Branded tooltip — replaces Joyride's default card so the tour matches the app
// (theme-aware surface, role accent, progress dots, explicit Skip).
const TourTooltip = ({ index, size, step, backProps, primaryProps, skipProps, closeProps, tooltipProps, isLastStep }) => {
  const accent = step?.data?.accent || '#6366f1';
  const showProgress = size > 1;
  return (
    <div
      {...tooltipProps}
      className="bg-surface border border-border rounded-xl shadow-2xl w-[340px] max-w-[calc(100vw-2rem)] overflow-hidden"
      data-testid="tour-tooltip"
    >
      <div className="px-4 pt-3.5 pb-3">
        <div className="flex items-center gap-2 mb-2">
          {showProgress && (
            <>
              <div className="flex items-center gap-1">
                {Array.from({ length: size }).map((_, i) => (
                  <span key={i} className="h-1 rounded-full transition-all"
                    style={{ width: i === index ? 14 : 5, backgroundColor: i <= index ? accent : 'var(--border)' }} />
                ))}
              </div>
              <span className="text-[10px] font-mono text-text-muted ml-0.5">{index + 1}/{size}</span>
            </>
          )}
          <button {...closeProps} className="ml-auto text-text-muted hover:text-text-primary" data-testid="tour-close">
            <X size={14} />
          </button>
        </div>
        <div className="font-heading font-bold text-[15px] text-text-primary mb-1">{step.title}</div>
        <div className="text-[13px] leading-relaxed text-text-secondary">{step.content}</div>
      </div>
      <div className="flex items-center gap-2 px-4 py-2.5 border-t border-border bg-background">
        {!isLastStep && (
          <button {...skipProps} className="text-[11px] text-text-muted hover:text-text-primary" data-testid="tour-skip">
            Skip tour
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          {index > 0 && (
            <button {...backProps} className="px-2.5 py-1.5 text-[12px] font-medium text-text-secondary hover:text-text-primary" data-testid="tour-back">
              Back
            </button>
          )}
          <button {...primaryProps}
            className="px-3.5 py-1.5 rounded-lg text-[12px] font-semibold text-white hover:opacity-90"
            style={{ backgroundColor: accent }}
            data-testid="tour-next">
            {isLastStep ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ROLE_BLURB = {
  vc: 'A quick lap around your dealflow cockpit — portfolio, programs and the AI co-pilot.',
  incubator: 'A quick lap around your cohort cockpit — programs, applicant triage and the AI co-pilot.',
  startup: 'A quick lap around your founder cockpit — dashboard, AI coach, programs and monthly updates.',
};

// First-login gate. We ASK before spotlighting anything: an unannounced tour that
// hijacks the screen is the thing users complain about.
const TourWelcomeModal = ({ role, stepTitles, onStart, onSkip }) => (
  <div className="fixed inset-0 z-[10001] bg-black/70 flex items-center justify-center p-4" data-testid="tour-welcome-modal">
    <div className="bg-surface border border-border rounded-xl shadow-2xl w-full max-w-[440px] overflow-hidden">
      <div className="p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Compass size={20} weight="bold" className="text-primary" />
          </div>
          <div className="min-w-0">
            <h2 className="font-heading font-bold text-lg text-text-primary">Want a quick tour?</h2>
            <p className="text-[13px] text-text-secondary mt-0.5">{ROLE_BLURB[role] || ROLE_BLURB.vc}</p>
          </div>
          <button onClick={onSkip} className="ml-auto text-text-muted hover:text-text-primary p-1" aria-label="Skip tour" data-testid="tour-welcome-close">
            <X size={16} />
          </button>
        </div>

        <div className="bg-background border border-border rounded-lg p-3 mb-4">
          <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-2">
            {stepTitles.length} stops · about a minute
          </div>
          <ul className="space-y-1">
            {stepTitles.map((t, i) => (
              <li key={t} className="text-[12px] text-text-secondary flex items-center gap-2">
                <span className="w-4 h-4 rounded-full bg-primary/10 text-primary font-mono text-[9px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={onStart}
            className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90"
            data-testid="tour-welcome-start">
            Take the tour
          </button>
          <button onClick={onSkip}
            className="px-4 py-2.5 text-sm font-medium text-text-secondary hover:text-text-primary"
            data-testid="tour-welcome-skip">
            Skip for now
          </button>
        </div>
      </div>
    </div>
  </div>
);

const OnboardingTour = ({ role }) => {
  // 'idle' → nothing on screen; 'welcome' → first-login ask; 'running' → the tour;
  // 'hint' → the one-step "here's how to replay it" spotlight.
  const [phase, setPhase] = useState('idle');
  const [steps, setSteps] = useState([]);
  const isReplayRef = useRef(false); // a re-run from the menu skips the replay hint
  const hintShownRef = useRef(false);
  const endHandledRef = useRef(false); // v3 can emit several terminal events per tour
  const navigate = useNavigate();
  const location = useLocation();

  // The burger-menu step closes every tour, so "where do I find this again?" is
  // answered by opening the actual menu rather than describing it.
  const roleSteps = [...(STEPS[role] || STEPS.vc), accountMenuStep(role), PRODUCT_TOUR_STEP];
  const accent = ROLE_ACCENT[role] || ROLE_ACCENT.vc;

  const beginTour = (isReplay) => {
    isReplayRef.current = isReplay;
    endHandledRef.current = false;
    setSteps(roleSteps);
    setPhase('running');
  };

  // Hold the nav rail open for the whole tour — collapsed, most steps would be
  // spotlighting unlabelled icons.
  useEffect(() => {
    if (TOUR_DISABLED) return;
    const active = phase === 'running' || phase === 'hint';
    pinSidebar(active);
    if (!active) clearCoHighlights();
    return () => { if (active) { pinSidebar(false); clearCoHighlights(); } };
  }, [phase]);

  // Re-run trigger from the account menu (or anywhere else).
  useEffect(() => {
    if (TOUR_DISABLED) return;
    const onStart = () => beginTour(true);
    window.addEventListener(PRODUCT_TOUR_EVENT, onStart);
    return () => window.removeEventListener(PRODUCT_TOUR_EVENT, onStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  useEffect(() => {
    if (TOUR_DISABLED) return;
    // Force-launch via URL ?tour=1 (deep links, support requests)
    if (new URLSearchParams(location.search).get('tour') === '1') {
      beginTour(true);
      return;
    }
    // Ask only once, ever. The localStorage fast-path avoids a round trip on every
    // page load; the server flag is the source of truth across devices.
    const token = localStorage.getItem('token');
    if (!token) return;
    let userId = '';
    try { userId = JSON.parse(localStorage.getItem('user') || '{}').id || ''; } catch { /* ignore */ }
    const promptedKey = `fb_tour_prompted_${userId}`;
    if (localStorage.getItem(promptedKey) === '1') return;
    api.get('/me/tour-status')
      .then(r => {
        if (r.data.tour_prompted || r.data.tour_seen || r.data.tour_skipped) {
          localStorage.setItem(promptedKey, '1');
          return;
        }
        // Flag the prompt as spent BEFORE showing it, so a reload mid-decision
        // doesn't ask again. What they choose is recorded separately.
        localStorage.setItem(promptedKey, '1');
        api.patch('/me/tour-status', { tour_prompted: true }).catch(() => {});
        setTimeout(() => setPhase('welcome'), 700);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // Skipping still owes the user the "here's how to get it back" pointer, so it
  // runs the closing step on its own.
  const finishFirstRun = (outcome) => {
    api.patch('/me/tour-status', outcome === 'skipped' ? { tour_skipped: true } : { tour_seen: true }).catch(() => {});
    if (outcome !== 'skipped' || hintShownRef.current) { setPhase('idle'); return; }
    hintShownRef.current = true;
    setPhase('idle');
    setTimeout(() => { endHandledRef.current = false; setPhase('hint'); }, 350);
  };

  const handleEvent = (data) => {
    const { status, type } = data;

    // Safety net — never leave the menu pinned open or an element ringed once the
    // tour stops, however it stopped.
    if (type === EVENTS.TOUR_END) { setAccountMenu(false); setAiBubble(false); clearCoHighlights(); }

    if (![STATUS.FINISHED, STATUS.SKIPPED].includes(status)) return;
    if (endHandledRef.current) return;
    endHandledRef.current = true;

    // Strip ?tour=1 from the URL so a refresh doesn't relaunch.
    if (new URLSearchParams(location.search).get('tour') === '1') {
      navigate(location.pathname, { replace: true });
    }

    if (phase === 'hint') { setPhase('idle'); return; }

    if (isReplayRef.current) {
      // A replay still counts as completing the tour, but never re-shows the hint.
      if (status === STATUS.FINISHED) api.patch('/me/tour-status', { tour_seen: true }).catch(() => {});
      setPhase('idle');
      return;
    }
    finishFirstRun(status === STATUS.SKIPPED ? 'skipped' : 'seen');
  };

  const activeSteps = phase === 'hint' ? [PRODUCT_TOUR_STEP] : steps;
  const isLight = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'light';

  if (TOUR_DISABLED) return null;

  return (
    <>
      {phase === 'welcome' && (
        <TourWelcomeModal
          role={role}
          stepTitles={roleSteps.map(s => s.title)}
          onStart={() => beginTour(false)}
          onSkip={() => finishFirstRun('skipped')}
        />
      )}

      {(phase === 'running' || phase === 'hint') && activeSteps.length > 0 && (
        <Joyride
          steps={activeSteps.map((s, i, arr) => {
            // The two closing steps both live inside the dropdown — don't close it
            // between them, or the menu flickers shut and reopens mid-tour.
            const nextNeedsMenu = !!arr[i + 1]?.requiresAccountMenu;
            return {
              target: s.target,
              title: s.title,
              content: s.content,
              placement: s.placement || 'right',
              data: { accent },
              before: async () => {
                // Open the panel first — the element we ring only exists once it is.
                // The bubble is hidden on a few routes (including the Coach page
                // itself), so don't stall waiting for a panel that can't mount.
                if (s.openAiBubble && document.querySelector('[data-testid="ai-bubble-fab"], [data-testid="ai-bubble-panel"]')) {
                  setAiBubble(true);
                  await waitForElement('[data-testid="ai-bubble-panel"]', 1200);
                }
                if (s.coHighlight) setCoHighlight(s.coHighlight, true);
                if (s.requiresAccountMenu) {
                  setAccountMenu(true);
                  await waitForElement(s.target);
                }
              },
              after: (data) => {
                if (s.coHighlight) setCoHighlight(s.coHighlight, false);
                if (s.openAiBubble) setAiBubble(false);
                const goingBack = data?.action === ACTIONS.PREV;
                if (s.requiresAccountMenu && (goingBack || !nextNeedsMenu)) setAccountMenu(false);
              },
            };
          })}
          run
          continuous
          tooltipComponent={TourTooltip}
          onEvent={handleEvent}
          options={{
            // v3 renamed `disableBeacon` — without this the tour parks an animated
            // dot on the first target and waits for a click instead of opening.
            skipBeacon: true,
            primaryColor: accent,
            zIndex: 10000,
            arrowColor: isLight ? '#ffffff' : '#111118',
            overlayColor: 'rgba(0,0,0,0.65)',
            spotlightRadius: 10,
            spotlightPadding: 6,
            targetWaitTimeout: 2500,
            buttons: ['back', 'skip', 'primary'],
          }}
        />
      )}
    </>
  );
};

export default OnboardingTour;
